/**
 * AutomationConfig Component
 *
 * Configuration component for automation settings.
 * Provides both form-based UI and JSON editor modes.
 * Renders different forms based on automation type (listExtractor, pageExtractor).
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 * @param {Object} props.taskConfig - Full task config (to access automationType)
 */

import React, { useState, useEffect } from "react";
import { VscJson } from "react-icons/vsc";
import { HiOutlineViewGrid } from "react-icons/hi";
import ListExtractorConfig from "./ListExtractorConfig";
import PageExtractorConfig from "./PageExtractorConfig";
import EmailExtractorConfig from "./EmailExtractorConfig";
import SocialLinkExtractorConfig from "./SocialLinkExtractorConfig";
import { AUTOMATION_TYPES } from "../../config/automationTypes";

const AutomationConfig = ({ config, onChange, taskConfig }) => {
  const automationType = taskConfig?.automationType;
  const automationTypeInfo = AUTOMATION_TYPES[automationType];
  const supportsFormMode =
    automationType === "listExtractor" ||
    automationType === "pageExtractor" ||
    automationType === "emailExtractor" ||
    automationType === "socialLinkExtractor";

  const [viewMode, setViewMode] = useState(supportsFormMode ? "form" : "json");
  const [jsonText, setJsonText] = useState(JSON.stringify(config || {}, null, 2));
  const [jsonError, setJsonError] = useState(null);

  // Sync jsonText when config changes externally (only in JSON mode or when switching)
  useEffect(() => {
    const configStr = JSON.stringify(config || {}, null, 2);
    if (configStr !== jsonText) {
      try {
        const currentParsed = JSON.parse(jsonText);
        if (JSON.stringify(currentParsed, null, 2) !== configStr) {
          setJsonText(configStr);
        }
      } catch {
        setJsonText(configStr);
        setJsonError(null);
      }
    }
  }, [config]);

  // Reset view mode when automation type changes
  useEffect(() => {
    if (supportsFormMode && viewMode === "json") {
      // Stay in JSON mode if user chose it
    } else if (!supportsFormMode) {
      setViewMode("json");
    }
  }, [automationType]);

  const handleJsonChange = (e) => {
    const newText = e.target.value;
    setJsonText(newText);

    try {
      const parsed = JSON.parse(newText);
      setJsonError(null);
      onChange(parsed);
    } catch (error) {
      setJsonError(error.message);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (error) {
      setJsonError(error.message);
    }
  };

  const handleModeSwitch = (newMode) => {
    if (newMode === "json" && viewMode === "form") {
      // Switching from form to JSON - serialize current config
      setJsonText(JSON.stringify(config || {}, null, 2));
      setJsonError(null);
    } else if (newMode === "form" && viewMode === "json") {
      // Switching from JSON to form - try to parse
      try {
        const parsed = JSON.parse(jsonText);
        setJsonError(null);
        onChange(parsed);
      } catch (error) {
        // If JSON is invalid, show error and don't switch
        setJsonError(error.message);
        return;
      }
    }
    setViewMode(newMode);
  };

  const renderFormEditor = () => {
    if (automationType === "listExtractor") {
      return <ListExtractorConfig config={config} onChange={onChange} />;
    }
    if (automationType === "pageExtractor") {
      return <PageExtractorConfig config={config} onChange={onChange} />;
    }
    if (automationType === "emailExtractor") {
      return <EmailExtractorConfig config={config} onChange={onChange} />;
    }
    if (automationType === "socialLinkExtractor") {
      return <SocialLinkExtractorConfig config={config} onChange={onChange} />;
    }
    return null;
  };

  const renderJsonEditor = () => (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Configuration JSON</label>
        <button
          onClick={handleFormat}
          className="px-2.5 py-1 rounded-lg text-xs font-medium
                   bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30
                   hover:bg-blue-200 dark:hover:bg-blue-500/30 hover:border-blue-300 dark:hover:border-blue-400/50
                   transition-colors"
        >
          Format
        </button>
      </div>

      <div className="relative">
        <textarea
          value={jsonText}
          onChange={handleJsonChange}
          rows={12}
          className={`w-full bg-gray-50 dark:bg-gray-700/60 text-green-600 dark:text-green-400 px-3 py-2.5 rounded-lg text-xs font-mono
                   border focus:outline-none resize-none leading-relaxed
                   ${jsonError ? "border-red-300 dark:border-red-500/50 focus:border-red-400" : "border-gray-300 dark:border-gray-600/50 focus:border-blue-500 dark:focus:border-blue-400"}`}
          spellCheck={false}
        />

        {jsonError && (
          <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg bg-red-100 dark:bg-red-900/80 border border-red-200 dark:border-red-500/40">
            <p className="text-xs text-red-600 dark:text-red-300 font-medium">Invalid JSON</p>
          </div>
        )}
      </div>

      {jsonError && (
        <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30">
          <p className="text-xs text-red-600 dark:text-red-300">
            <span className="font-medium">JSON Error:</span> {jsonError}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-2.5">
      {/* Mode Toggle with Automation Type Name */}
      {supportsFormMode && (
        <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {automationTypeInfo?.icon && (
                <div className={`p-1.5 rounded-lg ${automationTypeInfo.colorClasses?.text === 'text-cyan-400' ? 'bg-cyan-100 dark:bg-cyan-500/15' : automationTypeInfo.colorClasses?.text === 'text-violet-400' ? 'bg-violet-100 dark:bg-violet-500/15' : 'bg-gray-100 dark:bg-gray-500/15'}`}>
                  <automationTypeInfo.icon className={`w-3 h-3 ${automationTypeInfo.colorClasses?.text || 'text-gray-400'}`} />
                </div>
              )}
              <span className={`text-xs font-semibold uppercase tracking-wide ${automationTypeInfo?.colorClasses?.text || 'text-gray-400'}`}>
                {automationTypeInfo?.name || 'Automation'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { id: "form", label: "Form", icon: HiOutlineViewGrid },
                { id: "json", label: "JSON", icon: VscJson },
              ].map((mode) => (
                <button
                  key={mode.id}
                  className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors
                    ${viewMode === mode.id
                      ? "bg-blue-100 dark:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 text-blue-600 dark:text-blue-400"
                      : "bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/40 hover:bg-gray-200 dark:hover:bg-gray-800/70 text-gray-500 dark:text-gray-400"
                    }`}
                  onClick={() => handleModeSwitch(mode.id)}
                >
                  <mode.icon className="w-3 h-3" />
                  <span className="text-xs font-medium">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === "form" && supportsFormMode ? (
        renderFormEditor()
      ) : (
        <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
          {/* Info box for JSON mode */}
          <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 mb-3">
            <div className="flex items-start space-x-2">
              <VscJson className="w-3 h-3 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-2xs text-blue-700 dark:text-blue-200/80 leading-relaxed">
                {supportsFormMode
                  ? "Edit the automation configuration as JSON for advanced customization."
                  : "Edit the automation configuration as JSON. Form editing is not available for this automation type."}
              </div>
            </div>
          </div>
          {renderJsonEditor()}
        </div>
      )}

      {/* Helper info - only show in form mode */}
      {viewMode === "form" && supportsFormMode && (
        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/30">
          <p className="text-2xs text-gray-400 dark:text-gray-500">
            <span className="font-medium text-gray-500 dark:text-gray-400">Tip:</span> Switch to JSON mode for advanced configuration options.
          </p>
        </div>
      )}
    </div>
  );
};

export default AutomationConfig;
